// Phase 11 · Step 1: Amanda's packages, add-ons, payment terms and legal details.
// Source: "Ghostwriter Events — Coordination Services Agreement (Template)", Oct 9, 2026.
// Shared by the browser (Settings, quotes, contract preview) and the server functions.
// Defaults follow the plan's answers for questions 2 and 4–7 (Amanda's own answers can replace them in Settings).
// Prices (question 3) come from her pricing sheet, "Ghostwriter Events - Pricing Sheet.pdf" (Oct 15, 2026):
// Wedding Day $3,400, Partial $8,500, Full-Service custom from $14,000.

import { signersOf, isEmail } from "./signers.js";
export const PACKAGE_IDS = ["wedding-day", "partial", "full-service"];

export const DEFAULT_PACKAGES = [
  {
    id: "wedding-day",
    name: "Wedding Day Coordination",
    price: 3400, // null = quoted case by case
    priceFrom: null, // starting price when quoted case by case
    onSiteHours: 8,
    involvementStarts: "six (6) to eight (8) weeks before the Event Date",
    weeklyUpdates: false,
    inclusions: [
      "Review of event details and of Client’s wedding day timeline (Coordinator does not build the timeline under this package)",
      "Vendor confirmation calls and service as primary vendor point of contact for logistics",
      "Review of vendor contracts for logistical purposes only (see Section 3)",
      "Venue coordination and compliance with venue rules and restrictions",
      "Setup oversight and breakdown coordination",
      "Ceremony cues and flow management"
    ]
  },
  {
    id: "partial",
    name: "Partial Planning & Coordination",
    price: 8500,
    priceFrom: null,
    onSiteHours: 10,
    involvementStarts: "the Effective Date",
    weeklyUpdates: true,
    inclusions: [
      "Vendor sourcing for Client’s remaining vendors, with recommendations for Client’s decision",
      "Full wedding day timeline build-out, coordinated with the venue",
      "Monthly planning meetings (in person or virtual)",
      "Weekly email support during the Planning Period: a written status update at least once per week, and replies to Client emails within two (2) business days",
      "Vendor confirmation calls and service as primary vendor point of contact for logistics",
      "Review of vendor contracts for logistical purposes only (see Section 3)",
      "Venue coordination and compliance with venue rules and restrictions",
      "Rehearsal coordination",
      "Setup oversight and breakdown coordination",
      "Ceremony cues and flow management"
    ]
  },
  {
    id: "full-service",
    name: "Full-Service Planning & Design",
    price: null, // custom: priced to guest count, timeline and scope
    priceFrom: 14000,
    onSiteHours: 10,
    involvementStarts: "the Effective Date",
    weeklyUpdates: true,
    includes: "partial", // "everything in Option B, plus:"
    // Amanda's answer (Oct 23 checklist): her pricing sheet's list, so the contract adds venue selection and unlimited email support.
    inclusions: [
      "Venue selection assistance, with venue recommendations for Client’s decision",
      "Full vendor sourcing for all vendor categories",
      "Design and styling concept, including palette, décor direction, and vendor design briefs",
      "Budget planning and tracking against Client’s approved budget",
      "Monthly planning meetings (in person or virtual)",
      "Unlimited email support during the Planning Period: Client may email Coordinator as often as needed, and Coordinator replies within two (2) business days"
    ]
  }
];

// The Full-Service list as it shipped on Oct 22. A studio that saved Settings before her answer still carries it;
// normalizeSettings swaps it for the current list. A list she edited herself is left alone.
const FULL_SERVICE_OCT22 = [
  "Full vendor sourcing for all vendor categories",
  "Design and styling concept, including palette, décor direction, and vendor design briefs",
  "Budget planning and tracking against Client’s approved budget",
  "Monthly planning meetings (in person or virtual)",
  "Weekly email support during the Planning Period: a written status update at least once per week, and replies to Client emails within two (2) business days"
];

export const ADDON_UNITS = ["flat", "hour", "person"];

export const DEFAULT_ADDONS = [
  { id: "extra-hours", name: "Additional on-site hours", rate: 175, unit: "hour" },
  { id: "extra-coordinator", name: "Extra on-site coordinator", rate: 350, unit: "person" },
  { id: "rehearsal", name: "Rehearsal only (standalone)", rate: 450, unit: "flat", includedIn: ["partial", "full-service"] },
  { id: "vendor-sourcing", name: "Vendor sourcing only", rate: 1200, unit: "flat", note: "Included in Partial and Full-Service", includedIn: ["partial", "full-service"] },
  { id: "guest-lodging", name: "Guest lodging coordination", rate: 750, unit: "flat" },
  { id: "vows-toasts", name: "Vow and toast writing help", rate: 350, unit: "flat" }
];

export const DEFAULT_TERMS = {
  retainerPct: 50,
  retainerDueDays: 14,
  balanceDaysBefore: 30,
  latePauseDays: 7,
  cancelFullFeeWithinDays: 30,
  overtimeRate: 175,
  overtimeIncrementHrs: 0.5,
  maxOnSiteHours: 10,
  invoiceDueDays: 14,
  addonLateRequestDays: 14,
  postponeWithinMonths: 12,
  vendorMealDaysBefore: 21 // Phase 11 · Step 7: when she sends the vendor meal count (Section 7); her answer can replace it
};

export const DEFAULT_LEGAL = {
  legalName: "AD Freelance LLC",
  entityType: "a California limited liability company",
  dba: "Ghostwriter Events", // spelling as written in her contract
  role: "Coordinator",
  region: "Santa Barbara County",
  homeBase: "Central Coast, California",
  contactPhone: ""
};

const TERM_LIMITS = {
  retainerPct: [1, 100], retainerDueDays: [0, 365], balanceDaysBefore: [0, 365], latePauseDays: [0, 365],
  cancelFullFeeWithinDays: [0, 730], overtimeRate: [0, 100000], overtimeIncrementHrs: [0.25, 8], maxOnSiteHours: [1, 24],
  invoiceDueDays: [0, 365], addonLateRequestDays: [0, 365], postponeWithinMonths: [0, 60], vendorMealDaysBefore: [0, 365]
};

const clone = v => JSON.parse(JSON.stringify(v));
const num = (v, fallback, [lo, hi] = [-Infinity, Infinity]) => {
  const n = typeof v === "string" ? Number(v.replace(/[$,\s]/g, "")) : v;
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : fallback;
};
const priceOrNull = v => {
  if (v === null || v === undefined || v === "") return null;
  const n = num(v, null, [0, 10000000]);
  return n === null ? null : Math.round(n * 100) / 100;
};

/** Plain-English summaries generated from the numbers, so every screen, email and prompt says the same thing. */
export function termsSummary(terms) {
  const t = { ...DEFAULT_TERMS, ...(terms || {}) };
  const rest = 100 - t.retainerPct;
  const paymentTerms =
    t.retainerPct + "% non-refundable retainer, due within " + t.retainerDueDays + " days of the Effective Date; the date is held once it arrives. " +
    "The remaining " + rest + "% is due " + t.balanceDaysBefore + " days before the event, whether or not an invoice has been sent. " +
    "Services may be paused if a payment is more than " + t.latePauseDays + " days overdue.";
  const cancellationPolicy =
    "The retainer is non-refundable. Cancelling more than " + t.cancelFullFeeWithinDays + " days before the event: nothing further is owed. " +
    "Within " + t.cancelFullFeeWithinDays + " days: the full fee is due. " +
    "One postponement to a date within " + t.postponeWithinMonths + " months is free, at the same prices.";
  return { paymentTerms, cancellationPolicy };
}

function normalizePackage(p, fallback) {
  const base = fallback || {};
  const src = p && typeof p === "object" ? p : {};
  const inclusions = Array.isArray(src.inclusions) ? src.inclusions : base.inclusions || [];
  return {
    ...base,
    ...src,
    id: String(src.id || base.id),
    name: String(src.name || base.name || "").trim() || base.name,
    price: priceOrNull("price" in src ? src.price : base.price),
    priceFrom: priceOrNull("priceFrom" in src ? src.priceFrom : base.priceFrom),
    onSiteHours: num(src.onSiteHours, base.onSiteHours ?? 8, [0, 24]),
    involvementStarts: String(src.involvementStarts ?? base.involvementStarts ?? ""),
    weeklyUpdates: !!("weeklyUpdates" in src ? src.weeklyUpdates : base.weeklyUpdates),
    inclusions: inclusions.map(s => String(s).trim()).filter(s => s && !/^\[.*\]$/.test(s))
  };
}

function normalizeAddon(a) {
  if (!a || typeof a !== "object" || !String(a.name || "").trim()) return null;
  const def = DEFAULT_ADDONS.find(d => d.id === a.id) || {};
  return {
    ...(def.includedIn && !("includedIn" in a) ? { includedIn: def.includedIn } : {}),
    ...(def.note && !("note" in a) ? { note: def.note } : {}),
    ...a,
    id: String(a.id || slug(a.name)),
    name: String(a.name).trim(),
    rate: num(a.rate, 0, [0, 1000000]),
    unit: ADDON_UNITS.includes(a.unit) ? a.unit : "flat"
  };
}

export function slug(s) {
  return String(s || "").toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "item";
}

/**
 * Fills in anything Phase 11 added that older saved settings don't have, clamps numbers,
 * and regenerates the payment/cancellation summaries from `terms`. Idempotent.
 */
export function normalizeSettings(settings) {
  const s = settings && typeof settings === "object" ? settings : {};
  const savedPkgs = Array.isArray(s.packages) ? s.packages : [];
  const packages = DEFAULT_PACKAGES.map(def => {
    let saved = savedPkgs.find(p => p && p.id === def.id);
    if (saved && def.id === "full-service" && Array.isArray(saved.inclusions) &&
        saved.inclusions.map(x => String(x).trim()).join("\n") === FULL_SERVICE_OCT22.join("\n")) {
      saved = { ...saved, inclusions: def.inclusions.slice() };
    }
    return normalizePackage(saved, def);
  });
  savedPkgs.forEach(p => { if (p && p.id && !PACKAGE_IDS.includes(p.id) && p.name) packages.push(normalizePackage(p, { inclusions: [] })); });

  const addons = (Array.isArray(s.addons) ? s.addons : clone(DEFAULT_ADDONS)).map(normalizeAddon).filter(Boolean);
  const seen = new Set();
  addons.forEach(a => { let id = a.id, n = 2; while (seen.has(id)) id = a.id + "-" + n++; a.id = id; seen.add(id); });

  const terms = {};
  Object.keys(DEFAULT_TERMS).forEach(k => { terms[k] = num(s.terms && s.terms[k], DEFAULT_TERMS[k], TERM_LIMITS[k]); });

  const legal = {};
  Object.keys(DEFAULT_LEGAL).forEach(k => { legal[k] = typeof s[k] === "string" ? s[k] : DEFAULT_LEGAL[k]; });

  return { ...s, ...legal, packages, addons, terms, ...termsSummary(terms), settingsVersion: 11 };
}

/** Old free-text tier → package id. "Month-of" is treated as Wedding Day Coordination (question 2 default). */
const TIER_TO_PACKAGE = {
  "full service": "full-service",
  "full planning": "full-service",
  "full-service planning & design": "full-service",
  "partial planning": "partial",
  "partial planning & coordination": "partial",
  "month-of coordination": "wedding-day",
  "month of coordination": "wedding-day",
  "day-of coordination": "wedding-day",
  "day of coordination": "wedding-day",
  "wedding day coordination": "wedding-day"
};

export function packageIdForTier(tier) {
  if (!tier) return null;
  const k = String(tier).trim().toLowerCase();
  if (PACKAGE_IDS.includes(k)) return k;
  return TIER_TO_PACKAGE[k] || null;
}

/** Adds `packageId` to a client saved before Phase 11. Returns the same object when nothing changes. */
export function migrateClientPackage(client) {
  if (!client || typeof client !== "object" || "packageId" in client) return client;
  return { ...client, packageId: packageIdForTier(client.tier), addonIds: Array.isArray(client.addonIds) ? client.addonIds : [] };
}

export function migrateClients(list) {
  if (!Array.isArray(list)) return list;
  let changed = false;
  const next = list.map(c => { const m = migrateClientPackage(c); if (m !== c) changed = true; return m; });
  return changed ? next : list;
}

export function packageById(settings, id) {
  const pkgs = (settings && Array.isArray(settings.packages) && settings.packages.length) ? settings.packages : DEFAULT_PACKAGES;
  return pkgs.find(p => p.id === id) || null;
}

/** The name to show for a couple's package; falls back to the old tier text, then "Package not chosen". */
export function packageLabel(client, settings) {
  const p = client && client.packageId ? packageById(settings, client.packageId) : null;
  if (p) return p.name;
  return (client && client.tier && client.tier !== "Help me choose") ? client.tier : "Package not chosen";
}

/** Task-template key used by the Tasks module for a package. */
export function taskTemplateFor(client) {
  const id = client && (client.packageId || packageIdForTier(client.tier));
  return id === "partial" ? "Partial Planning" : id === "wedding-day" ? "Day-of Coordination" : "Full Planning";
}

export function formatRate(addon) {
  const amt = "$" + Number(addon.rate || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
  return addon.unit === "hour" ? amt + " per hour" : addon.unit === "person" ? amt + " per person" : amt + " flat";
}

/* ───────── Phase 11 · Step 2: the couple's agreement details ───────── */

/** Package choices offered to couples in the questionnaire. */
export function packageChoices(settings) {
  const pkgs = (settings && Array.isArray(settings.packages) && settings.packages.length) ? settings.packages : DEFAULT_PACKAGES;
  return pkgs.map(p => ({ id: p.id, name: p.name }));
}

/** "16:30" → "4:30 PM". Free text like "4:30 PM" or "around sunset" is kept as typed. */
export function formatCeremonyTime(v) {
  const s = String(v || "").trim();
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) return s;
  const h = Number(m[1]), min = m[2];
  if (h > 23 || Number(min) > 59) return s;
  return ((h % 12) || 12) + ":" + min + " " + (h < 12 ? "AM" : "PM");
}

/** "Oct 10, 2026" / "2026-10-10" → true when it's a real calendar date (not "To be set"). */
export function hasRealDate(v) {
  const s = String(v || "").trim();
  if (!s) return false;
  const t = Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(s) ? s + "T12:00:00" : s);
  return Number.isFinite(t);
}

/**
 * What the agreement still needs from the couple's profile (Section 1 and the package), as labels
 * for the planner. Empty array = ready to fill in the contract.
 */
export function agreementGaps(client, settings) {
  const c = client || {};
  const p = Array.isArray(c.partners) ? c.partners : [];
  const gaps = [];
  const has = v => String(v == null ? "" : v).trim() !== "";
  if (!has(p[0] && p[0].name)) gaps.push("Partner 1's name");
  if (!has(p[1] && p[1].name)) gaps.push("Partner 2's name");
  // Phase 12 · Batch C: the agreement's contact details are the signers' (the partners, unless she set others).
  const signers = signersOf(c);
  const who = signers.length ? signers : p;
  // Every signer she set gets the agreement, the signed copy and payment reminders, so each needs an email.
  const explicit = Array.isArray(c.signers) && c.signers.length > 0;
  if (explicit) signers.forEach(x => { if (!isEmail(x.email)) gaps.push("An email for " + (has(x.name) ? x.name.trim() : "the signer")); });
  else if (!who.some(x => x && has(x.email))) gaps.push("An email address");
  if (!who.some(x => x && has(x.phone))) gaps.push("A phone number");
  if (!hasRealDate(c.date)) gaps.push("Wedding date");
  if (!has(c.ceremonyTime)) gaps.push("Ceremony start time");
  if (!has(c.venue) || /^(TBD|Venue —)/.test(String(c.venue))) gaps.push("Venue name");
  if (!has(c.venueAddress)) gaps.push("Venue address");
  if (!(Number(c.guests) > 0)) gaps.push("Guest count");
  if (!c.packageId || !packageById(settings, c.packageId)) gaps.push("Package");
  return gaps;
}

/** Add-ons a couple can choose: drops any that the chosen package already includes. */
export function addonsFor(settings, packageId) {
  const list = (settings && Array.isArray(settings.addons)) ? settings.addons : DEFAULT_ADDONS;
  return list.filter(a => !(Array.isArray(a.includedIn) && packageId && a.includedIn.includes(packageId)));
}
